import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { chartColors } from './chartColors';
import { themeColor } from '../../../util/theme';

const defaultColors = () => chartColors('blue', 'retail', 'orange', 'green', 'purple', 'red', 'amber', 'slate');

/**
 * Reusable donut chart with right-side legend
 * @param {Array} data - Array of { name, value, color? } objects
 * @param {function} formatValue - Optional formatter for legend values (default: number)
 * @param {string} valuePrefix - Optional prefix like "$"
 * @param {string} valueSuffix - Optional suffix like "%"
 * @param {boolean} showPercent - Show percentage in legend (default true)
 * @param {Array} colors - Optional color array override
 */
const DonutChart = ({
  data = [],
  formatValue,
  valuePrefix = '',
  valueSuffix = '',
  showPercent = true,
  colors = defaultColors()
}) => {
  if (!data || data.length === 0) {
    return <div style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '20px 0' }}>No data</div>;
  }

  const total = data.reduce((sum, d) => sum + (d.value || 0), 0);

  const displayValue = (val) => {
    if (formatValue) return formatValue(val);
    return `${valuePrefix}${val.toLocaleString()}${valueSuffix}`;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
      <div style={{ width: 140, height: 140, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={35}
              outerRadius={60}
              dataKey="value"
              strokeWidth={2}
              stroke={themeColor('--color-bg-card', '#fff')} // theme-ignore-line
              isAnimationActive={false}
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.color || colors[i % colors.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {data.map((entry, i) => {
          const pct = total > 0 ? Math.round((entry.value / total) * 100) : 0;
          return (
            <div key={i} style={{ marginBottom: entry.sublabel ? 6 : 4, fontSize: 13 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                  backgroundColor: entry.color || colors[i % colors.length]
                }} />
                <span style={{ flex: 1, color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {entry.name}
                </span>
                <span style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                  {displayValue(entry.value)}
                  {showPercent && <span style={{ marginLeft: 4, color: 'var(--color-text-muted)' }}>({pct}%)</span>}
                </span>
              </div>
              {entry.sublabel && (
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 18, marginTop: 1 }}>{entry.sublabel}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DonutChart;
